/**
 * Origin D "เวทีงิ้วโคมเก้าสาย": the full chain from the wake in the troupe's yard to the sect choice, as data
 * (docs/design/2026-10-13-origin-d-chain.md; beats from docs/design/C0_TOWNS_BCD.md §5.5 on the 96 m greybox town-d).
 * The spine is the challenger queue: five fighters, each a fair bout (`bout`) that tests one duel idea (block, guard
 * break, dash dodge, counter, then the final). Run by systems/originFull.ts. Coordinates are tiles of town-d (greybox,
 * data/layouts/townGrey.ts) or of the 256 m plains (the last two steps, by the stream). `{m:…|f:…}` picks the hero's sex.
 * The masks stay unnamed (LORE P12).
 */
import { FERRYMAN, LOG_SPOT, STARTER_HORSE, STARTER_LOG } from './originChains';
import type { FullChainDef, FullStep } from './originFull';
import type { QuestDef } from '../quests';

const Z = 'town-d';
const P = 'plains';
const q = (s: string): string => `og-d-${s}`;

/** Step ids, in order. */
export const OD = {
  wake: q('wake'),
  first: q('first'),
  register: q('register'),
  calm: q('calm'),
  fan: q('fan'),
  sly: q('sly'),
  chase: q('chase'),
  roost: q('roost'),
  counter: q('counter'),
  final: q('final'),
  horse: q('horse'),
  gate: q('gate'),
  bamboo: q('bamboo'),
  banners: q('banners'),
} as const;

/** The four sects whose envoys wait on the stream's east bank (all are joinable schools). */
export const OD_SECTS = ['pine-ink', 'mist-peak', 'bamboo-tide', 'lantern-road'] as const;

const quest = (
  id: string,
  title: string,
  offer: string,
  remind: string,
  done: string,
  objectives: QuestDef['objectives'],
  cultivation: number,
  gold: number,
  giver?: QuestDef['giver'],
): QuestDef => ({ id, title, offer, remind, done, objectives, rewards: { cultivation, gold }, ...(giver ? { giver } : {}) });

const JING = 'ป้าจิ้ง';
const GONG = 'ลุงกงสามเสียง';
const CUI = 'ครูพัดชุ่ย';
const HE = 'เสี่ยวเหอ';
const HOW = 'คุยกับเขาเพื่อรับคำท้า แพ้ก็ไม่เสียอะไร คุยใหม่เพื่อท้าอีกครั้ง';

const steps: FullStep[] = [
  {
    zone: Z,
    startAt: [16.5, 16.5],
    guide: { spot: 'basin' },
    ordered: true,
    talkLines: {
      jing: '"ได้ยินไหม ข้างนอกมีคนมาท้าเจ้าแล้ว รอยแดงกลางเวทีเมื่อคืน คนทั้งเมืองเห็นหมด" ป้าชี้ไปทางประตูคณะ "ไปเถอะ แต่อย่าเพิ่งตาย คืนนี้ยังมีเล่นอีกรอบ"',
    },
    quest: quest(
      OD.wake,
      'หน้ายังเป็นงิ้ว',
      'เจ้าตื่นในเรือนคณะงิ้ว หน้ายังเป็นงิ้วจากเมื่อคืน ฝ่ามือมีรอยแดงที่ขึ้นกลางเวทีต่อหน้าคนทั้งเมือง · ' +
        `${JING}ตะโกน: "ตื่น! ล้างหน้าก่อน หน้าเจ้ายังเป็นงิ้วอยู่"`,
      `ไปล้างหน้าที่อ่างน้ำมุมลาน แล้วคุยกับ${JING}`,
      'ประตูคณะถูกทุบดังปัง ปัง ปัง',
      [
        { type: 'visit', target: 'basin', count: 1, label: 'ล้างหน้าที่อ่างน้ำ' },
        { type: 'talk', target: 'jing', count: 1, label: `คุยกับ${JING}` },
      ],
      100,
      10,
      { name: JING, where: 'ในลานคณะงิ้ว' },
    ),
  },
  {
    zone: Z,
    guide: { npc: 'dahu' },
    bout: { npc: 'dahu', styleId: 'fierce', level: 0, label: 'ผู้ท้าคนที่ 1 · หน้าประตูคณะ' },
    quest: quest(
      OD.first,
      'ผู้ท้าคนแรก',
      'ต้าหูยืนเท้าสะเอวหน้าประตู "ศิลาเลือกเจ้าหรือ งั้นมาดูกันว่าเลือกถูกไหม" · ' +
        `เขาบุกไม่หยุด ตั้งรับ (F) ตอนเขาง้าง แล้วค่อยสวนกลับ · ${HOW}`,
      'ประลองกับต้าหูหน้าประตูคณะ ตั้งรับให้ได้สองครั้งแล้วชนะเขา',
      'ต้าหูนั่งแปะกับพื้น "ฟลุก! พรุ่งนี้ข้ามาใหม่" เสียงเชียร์ดังจากหน้าประตู',
      [
        { type: 'block', count: 2, label: 'ตั้งรับตอนต้าหูง้าง' },
        { type: 'duelWin', count: 1, label: 'ชนะประลองต้าหู' },
      ],
      250,
      30,
      { name: 'ต้าหู', where: 'หน้าประตูคณะงิ้ว' },
    ),
  },
  {
    zone: Z,
    guide: { npc: 'gong' },
    talkLines: {
      gong:
        '"เสียงที่หนึ่ง: ยินดีต้อนรับสู่ลานประลองเทศกาล! เสียงที่สอง: ลงชื่อแล้วห้ามถอน! เสียงที่สาม: ไอ้หน้างิ้วนี่จะรอดถึงรอบชิงไหมนะ!" · ' +
        'ข้างหลังลุงมีนักสู้ต่อแถวรอท้าเจ้าอยู่แล้ว',
    },
    quest: quest(
      OD.register,
      'ลงชื่อลานเทศกาล',
      'คนมาท้าเจ้าไม่หยุด จะสู้กันกลางถนนก็ไม่งาม ไปลงชื่อที่ลานประลองเทศกาลทางใต้ของลานกลางเมือง ให้ผู้คุมลานจัดคิวให้',
      `ไปหา${GONG} ผู้คุมลานประลองเทศกาล ทางใต้ของลานกลางเมือง`,
      'ชื่อเจ้าถูกเขียนบนกระดานด้วยหมึกแดง นักสู้สี่คนต่อแถวรอ',
      [{ type: 'talk', target: 'gong', count: 1, label: `ลงชื่อกับ${GONG}` }],
      150,
      20,
      { name: GONG, where: 'ที่ลานประลองเทศกาล' },
    ),
  },
  {
    zone: Z,
    guide: { npc: 'zhu' },
    bout: { npc: 'zhu', styleId: 'calm', level: 0, label: 'ผู้ท้าคนที่ 2 · ลานประลองเทศกาล' },
    quest: quest(
      OD.calm,
      'ผู้ท้าใจนิ่ง',
      'จูชิงยืนนิ่งเหมือนน้ำในโอ่ง "ข้าไม่บุก ข้ารอ" · เขาตั้งรับเกือบตลอด ฟาดใส่การตั้งรับก็เปล่าแรง ใช้ท่าทำลายตัดการ์ดเขาก่อน · ' +
        HOW,
      'ประลองกับจูชิงที่ลาน ใช้ท่าทำลายตอนเขาตั้งรับ แล้วชนะเขา',
      'จูชิงประสานมือ "การ์ดข้าแตกเพราะเจ้ารอได้นานกว่า" ผู้จดหอหมึกสนกระซิบ "บันทึกไว้แล้ว"',
      [
        { type: 'guardBreak', count: 1, label: 'ท่าทำลายตัดการ์ดจูชิง' },
        { type: 'duelWin', count: 1, label: 'ชนะประลองจูชิง' },
      ],
      350,
      40,
      { name: 'จูชิง', where: 'ในแถวผู้ท้าที่ลาน' },
    ),
  },
  {
    zone: Z,
    guide: { npc: 'cui' },
    grantQinggong: 'swallow-dash',
    talkLines: {
      cui: '"เจ้าตั้งรับเป็น ตัดการ์ดเป็น แต่ยังยืนรับทุกอย่าง" ครูกางพัดเหล็ก "บนเวที คนที่หลบเป็นไม่ต้องรับ · ย่อเข่า ปล่อยลมหายใจ แล้วพุ่ง นี่คือนกนางแอ่นพุ่งตัว ขณะพุ่ง ไม่มีอะไรโดนเจ้า"',
    },
    quest: quest(
      OD.fan,
      'พัดของคณะ',
      `${CUI}เคยเล่นบทนางเอกบู๊ของคณะ ตอนนี้สอนอยู่ที่โรงฝึกยุทธ์เหนือลาน นางส่งเด็กมาตาม "คนต่อไปในแถวเจ้าเล่ห์ ไปเรียนหลบก่อน"`,
      `ไปหา${CUI}ที่ประตูโรงฝึกยุทธ์ เหนือลานกลางเมือง`,
      'ได้วิชาตัวเบา นกนางแอ่นพุ่งตัว (กดพุ่งตัว ขณะพุ่งหลบได้ทุกการโจมตี)',
      [{ type: 'talk', target: 'cui', count: 1, label: `เรียนวิชาตัวเบากับ${CUI}` }],
      200,
      30,
      { name: CUI, where: 'ที่โรงฝึกยุทธ์' },
    ),
  },
  {
    zone: Z,
    guide: { npc: 'feng' },
    bout: { npc: 'feng', styleId: 'sly', level: 1, label: 'ผู้ท้าคนที่ 3 · ลานประลองเทศกาล' },
    quest: quest(
      OD.sly,
      'ผู้ท้าเจ้าเล่ห์',
      'ถังเฟยเดินเข้าออกไม่เป็นจังหวะ "อย่ามองมือข้า มองเท้าข้า… หรือไม่ต้องมองก็ได้" · ' +
        `จังหวะที่เขาฟันเข้ามา ให้พุ่งตัวผ่านคมไปหนึ่งครั้ง · ${HOW}`,
      'ประลองกับถังเฟย พุ่งตัวหลบคมของเขาให้ได้หนึ่งครั้ง แล้วชนะเขา',
      'ถังเฟยหัวเราะ "หลบเป็นแล้วนี่" · ระหว่างเสียงเชียร์ มีมือหนึ่งล้วงห่อบทงิ้วจากเอวเจ้าไป',
      [
        { type: 'dodge', count: 1, label: 'พุ่งตัวหลบคมถังเฟย' },
        { type: 'duelWin', count: 1, label: 'ชนะประลองถังเฟย' },
      ],
      400,
      40,
      { name: 'ถังเฟย', where: 'ในแถวผู้ท้าที่ลาน' },
    ),
  },
  {
    zone: Z,
    guide: { spot: 'mark1' },
    timer: { seconds: 30, failTo: [61.5, 49.5], failText: 'หัวขโมยหายลับไปแล้ว · ตั้งต้นใหม่ที่หน้าโรงฝึกยุทธ์' },
    quest: quest(
      OD.chase,
      'หัวขโมยบนเชือกโคม',
      'หนูมือไวคว้าบทงิ้วทั้งเล่มวิ่งขึ้นเชือกโคมไปทางตะวันออก! บทเล่มนั้นคณะเล่นมาสามชั่วคน ไล่ให้ทัน ใช้ท่าพุ่งตัวที่ครูสอน',
      'ตามหัวขโมยไปทางตะวันออก ผ่านเสาโคมสามต้นให้ทันเวลา',
      'หนูมือไวกระโดดลงลานหลังโกดังตะวันออก มีนักเลงรออยู่สามคน',
      [{ type: 'route', target: 'chase', count: 3, label: 'ผ่านเสาโคมตามหัวขโมย (มีเวลาจำกัด)' }],
      300,
      30,
    ),
  },
  {
    zone: Z,
    guide: { spot: 'roost' },
    ordered: true,
    spawns: [
      { monster: 'bandit', at: [80.5, 18.5], name: 'นักเลงรับจ้าง' },
      { monster: 'bandit', at: [85.5, 21.5], name: 'นักเลงรับจ้าง' },
      { monster: 'bandit', at: [78.5, 23.5], name: 'นักเลงรับจ้าง' },
    ],
    talkLines: {
      thief:
        '"อย่าตี! มีคนจ้างข้า… ชายใส่หน้ากากไม้ เขาอยากได้แค่หน้าสุดท้าย" · หน้าสุดท้ายของบทเขียนว่า "เรื่องจบที่ลานศิลาเก้ากระบี่" ในห่อบทมีป้ายไม้แกะเป็นหน้ากากตกอยู่',
    },
    quest: quest(
      OD.roost,
      'รังนักเลงท่าโคม',
      'นักเลงรับจ้างสามคนขวางอยู่หน้าหัวขโมย นี่ไม่ใช่การประลอง ไม่มีใครนับยก · มันง้างก่อนตีเสมอ ระวังถูกรุม',
      'จัดการนักเลงรับจ้างในลานตะวันออก แล้วเอาบทงิ้วคืนจากหนูมือไว',
      'ได้บทงิ้วคืน · หน้าสุดท้ายชี้ไปที่ลานศิลาเก้ากระบี่ และป้ายหน้ากากไม้ที่ไม่มีใครในคณะรู้จัก',
      [
        { type: 'kill', target: 'bandit', count: 3, label: 'นักเลงรับจ้าง' },
        { type: 'talk', target: 'thief', count: 1, label: 'เอาบทงิ้วคืนจากหนูมือไว' },
      ],
      450,
      60,
    ),
  },
  {
    zone: Z,
    guide: { npc: 'liang' },
    bout: { npc: 'liang', styleId: 'fierce', level: 1, label: 'ผู้ท้าคนที่ 4 · ลานตะวันออก' },
    quest: quest(
      OD.counter,
      'ผู้ท้าตามมาถึงที่',
      'เหลียงกวนเดินตามมาถึงลานตะวันออก "คิวข้ายาวเกินไป ข้ามาท้าถึงที่เลย" · มือเขาหนัก ตั้งรับอย่างเดียวการ์ดแตกแน่ · ' +
        `กดท่าเสริมหมอกลมคลุมกาย (ปุ่ม 6) ก่อนเขาฟันถึงตัว แล้วสวนกลับ · ${HOW}`,
      'ประลองกับเหลียงกวน สวนกลับให้ได้หนึ่งครั้งแล้วชนะเขา',
      'เหลียงกวนสะบัดมือที่ชา "สวนได้เฉียบ รอบชิงคืนนี้ข้าจะไปเชียร์เจ้า"',
      [
        { type: 'counter', count: 1, label: 'สวนกลับเหลียงกวน' },
        { type: 'duelWin', count: 1, label: 'ชนะประลองเหลียงกวน' },
      ],
      500,
      60,
      { name: 'เหลียงกวน', where: 'ที่ลานตะวันออก' },
    ),
  },
  {
    zone: Z,
    guide: { npc: 'he' },
    bout: { npc: 'he', styleId: 'calm', level: 2, label: 'รอบชิงเทศกาลโคมเก้าสาย' },
    quest: quest(
      OD.final,
      'รอบชิงเทศกาล',
      `โคมเก้าสายติดทีละตรอก ${GONG}ตีฆ้องสามครั้ง "รอบชิง!" คู่ชิงคือ${HE} ตัวรองของคณะ ผู้ที่ควรได้บทของเจ้าเมื่อคืน · ` +
        `เขาอ่านเกมเก่ง ใช้ทุกอย่างที่เรียนมา ตั้งรับ ตัดการ์ด พุ่งตัวหลบ สวนกลับ · ${HOW}`,
      `ประลองรอบชิงกับ${HE}ที่ลานประลองเทศกาล`,
      `${HE}ยื่นมือดึงเจ้าลุก "บทนั้นเป็นของเจ้าจริงๆ" · ผู้แทนสำนักที่ดูอยู่บนอัฒจันทร์ฝากคำไว้ คืนนี้พวกเขาจะรอที่ฝั่งตะวันออกของลำธารกลางทุ่ง`,
      [{ type: 'duelWin', count: 1, label: `ชนะรอบชิงกับ${HE}` }],
      700,
      120,
      { name: HE, where: 'ที่ลานประลองเทศกาล' },
    ),
  },
  {
    zone: Z,
    guide: { npc: 'groom' },
    ordered: true,
    grantMount: { npc: 'groom', mount: STARTER_HORSE },
    talkLines: {
      groom: 'ม้าลากเกวียนคณะตัวนี้ขี่ได้ดีกว่าลาก ป้าจิ้งสั่งให้ยกให้แชมป์ กดปุ่มขี่พาหนะเพื่อขึ้น กดอีกครั้งเพื่อลง ขี่ไปลานตะวันตกให้มันคุ้นมือก่อน',
    },
    quest: quest(
      OD.horse,
      'ม้าของคณะ',
      'ฝั่งตะวันออกของลำธารไกลเกินเดิน ลุงคอกม้าคณะเตรียมม้าไว้ให้ตัวหนึ่งที่คอกข้างลาน ขอม้าแล้วลองขี่ข้ามเมืองให้คล่อง',
      'คุยกับลุงคอกม้าคณะที่คอกม้า แล้วขี่ม้าไปที่ลานตะวันตก',
      'ม้ารู้ใจเจ้าแล้ว ม้าตัวนี้เป็นของเจ้า',
      [
        { type: 'talk', target: 'groom', count: 1, label: 'รับม้าจากลุงคอกม้าคณะ' },
        { type: 'visit', target: 'horseRide', count: 1, label: 'ขี่ม้าไปลานตะวันตก' },
      ],
      300,
      60,
      { name: 'ลุงคอกม้าคณะ', where: 'ที่คอกม้า' },
    ),
  },
  {
    zone: Z,
    guide: { spot: 'gate' },
    timer: { seconds: 40, failTo: [16.5, 70.5], failText: 'ยามโคมปิดประตูเหนือแล้ว! ถอยกลับลานตะวันตกแล้วควบใหม่' },
    spawns: [
      { monster: 'bandit', at: [30.5, 60.5], name: 'ผู้ท้าที่แพ้ไม่ยอม' },
      { monster: 'bandit', at: [40.5, 30.5], name: 'ผู้ท้าที่แพ้ไม่ยอม' },
    ],
    quest: quest(
      OD.gate,
      'ควบออกประตูเหนือ',
      'ยามโคมจะปิดประตูเหนือเมื่อโคมดวงสุดท้ายติด และพวกที่แพ้เจ้าวันนี้บางคนไม่ยอมแพ้ ขึ้นม้าแล้วควบออกไปให้ทัน',
      'ขี่ม้าไปประตูเหนือให้ทันเวลา (ต้องอยู่บนหลังม้า)',
      'ม้าพุ่งผ่านประตูก่อนบานจะปิด ถนนนาข้าวพาไปทุ่งราบ',
      [{ type: 'visit', target: 'gate', count: 1, label: 'ขี่ม้าออกประตูเหนือ' }],
      350,
      60,
    ),
  },
  {
    zone: P,
    guide: { npc: 'ferryman' },
    ordered: true,
    grantMount: { npc: 'ferryman', mount: STARTER_LOG },
    spawns: [
      { monster: 'bandit', at: [155.5, 124.5], name: 'นักเลงเฝ้าสะพาน' },
      { monster: 'bandit', at: [155.5, 127.5], name: 'นักเลงเฝ้าสะพาน' },
    ],
    talkLines: {
      ferryman:
        'นักเลงจากเมืองโคมมาดักบนสะพาน คงตามบทงิ้วของเจ้ามา ไม้ไผ่ท่อนนี้ยกให้ เลือกไผ่ลำเดียวเป็นพาหนะ ยืนบนลำแล้วพายลงกลางน้ำ ผู้แทนสำนักรออยู่ฝั่งตะวันออก',
    },
    quest: quest(
      OD.bamboo,
      'ไผ่ลำเดียว',
      'ลำธารกลางทุ่งขวางทาง และมีนักเลงดักบนสะพาน ลุงพายไผ่ริมน้ำมีไม้ไผ่ให้หนึ่งท่อน ยืนบนมันแล้วพายข้ามไปฝั่งตะวันออก',
      'คุยกับลุงพายไผ่ริมลำธาร ขึ้นไม้ไผ่ พายลงกลางน้ำ แล้วขึ้นฝั่งตะวันออก',
      'เท้าแตะฝั่งตะวันออก ธงสี่สำนักโบกอยู่ข้างหน้า',
      [
        { type: 'talk', target: 'ferryman', count: 1, label: 'รับไม้ไผ่จากลุงพายไผ่' },
        { type: 'visit', target: 'logMid', count: 1, label: 'ยืนพายไม้ไผ่ลงกลางลำธาร' },
        { type: 'visit', target: 'eastBank', count: 1, label: 'ขึ้นฝั่งตะวันออก' },
      ],
      350,
      60,
      { name: FERRYMAN.name, where: 'ริมลำธารกลางทุ่ง' },
    ),
  },
  {
    zone: P,
    guide: { npc: 'gu' },
    quest: quest(
      OD.banners,
      'ผู้แทนที่ริมธาร',
      'ผู้จดกู้ซื่อแห่งหอหมึกสนกางสมุด "ห้ายก ข้าบันทึกไว้ครบ" · ผู้แทนสี่สำนักที่ดูรอบชิงรอเจ้าอยู่ที่นี่ ' +
        `คุยกับแต่ละคนแล้วเลือกทางของเจ้า หรือจะเดินคนเดียวไปกับ${HE}ก็ได้`,
      `คุยกับผู้แทนสำนักที่ฝั่งตะวันออก แล้วเข้าร่วมสำนัก · หรือบอก${HE}ว่าจะเดินคนเดียว`,
      'ทางของเจ้าเริ่มแล้ว · หน้าสุดท้ายของบทยังชี้ไปที่ลานศิลาเก้ากระบี่ และป้ายหน้ากากไม้ยังอยู่ในห่อ',
      [{ type: 'sect', count: 1, label: 'เข้าร่วมสำนัก หรือเดินคนเดียว' }],
      500,
      100,
    ),
  },
];

export const ORIGIN_D: FullChainDef = {
  origin: 'd',
  steps,
  npcs: [
    {
      id: 'jing',
      zone: Z,
      name: JING,
      title: 'เจ้าคณะงิ้วโคมเก้าสาย',
      sprite: 'farmerAunt',
      at: [14.5, 18.5],
      idle: ['ซ้อมบทด้วยนะ คืนนี้ยังมีเล่นอีกรอบ', 'คนทั้งเมืองมาดูรอยของเจ้า ไม่ได้มาดูงิ้ว ช่างมันเถอะ ตั๋วขายหมด'],
    },
    {
      id: 'dahu',
      zone: Z,
      name: 'ต้าหู',
      title: 'ผู้ท้าคนที่ 1',
      sprite: 'heroBlood',
      at: [25.5, 21.5],
      facing: -1,
      idle: ['พรุ่งนี้ข้ามาใหม่!', 'คราวหน้าข้าจะไม่ง้างให้เห็นหรอก'],
      after: { step: OD.first, to: [40.5, 67.5] },
    },
    {
      id: 'gong',
      zone: Z,
      name: GONG,
      title: 'ผู้คุมลานประลองเทศกาล',
      sprite: 'innkeeper',
      at: [44.5, 61.5],
      idle: ['เสียงที่หนึ่ง: คิวต่อไป! เสียงที่สอง: ห้ามแซง! เสียงที่สาม: ข้าเชียร์เจ้านะ', 'ลานนี้ใครก็ท้าได้ แต่ต้องลงชื่อก่อน'],
    },
    {
      id: 'zhu',
      zone: Z,
      from: OD.register,
      name: 'จูชิง',
      title: 'ผู้ท้าคนที่ 2 · ใจนิ่ง',
      sprite: 'heroWudang',
      at: [52.5, 64.5],
      facing: -1,
      idle: ['น้ำนิ่งไม่ได้แปลว่าตื้น', 'การ์ดที่แตก ยังซ่อมได้'],
      after: { step: OD.calm, to: [38.5, 65.5] },
    },
    {
      id: 'feng',
      zone: Z,
      from: OD.register,
      name: 'ถังเฟย',
      title: 'ผู้ท้าคนที่ 3 · เจ้าเล่ห์',
      sprite: 'knifer',
      at: [55.5, 65.5],
      facing: -1,
      idle: ['ข้าไม่ได้โกงนะ ข้าแค่เร็ว', 'เจ้าเห็นคนที่ขโมยบทเจ้าไหม ข้าก็ไม่เห็น'],
      after: { step: OD.sly, to: [36.5, 63.5] },
    },
    {
      id: 'he',
      zone: Z,
      from: OD.register,
      name: HE,
      title: 'ตัวรองคณะงิ้ว · คู่ชิง',
      sprite: 'heroWudangPalm',
      at: [58.5, 66.5],
      facing: -1,
      idle: ['บทนั้นข้าซ้อมมาสามปี แต่ศิลาเลือกเจ้า', 'รอบชิงเจอกัน อย่าแพ้คนอื่นก่อนล่ะ'],
    },
    {
      id: 'cui',
      zone: Z,
      name: CUI,
      title: 'ครูโรงฝึกยุทธ์ · อดีตนางเอกบู๊',
      sprite: 'blissLady',
      at: [61.5, 47.5],
      idle: ['บนเวที คนที่หลบเป็นไม่ต้องรับ', 'พุ่งให้ตรงจังหวะ ไม่ใช่พุ่งให้ไกล'],
    },
    {
      id: 'thief',
      zone: Z,
      from: OD.chase,
      name: 'หนูมือไว',
      title: 'หัวขโมยบนเชือกโคม',
      sprite: 'townKid',
      at: [82.5, 20.5],
      idle: ['ข้าคืนแล้วนะ! อย่าบอกยามโคม'],
    },
    {
      id: 'liang',
      zone: Z,
      from: OD.counter,
      name: 'เหลียงกวน',
      title: 'ผู้ท้าคนที่ 4 · มือหนัก',
      sprite: 'heroShaolin',
      at: [78.5, 28.5],
      idle: ['สวนได้เฉียบ', 'คืนนี้ข้าไปเชียร์รอบชิง'],
    },
    {
      id: 'groom',
      zone: Z,
      name: 'ลุงคอกม้าคณะ',
      title: 'คนเลี้ยงม้าคณะงิ้ว',
      sprite: 'stationMaster',
      at: [70.5, 57.5],
      idle: ['ม้าคณะเคยลากเกวียนข้ามเก้าเมือง ทางไกลมันไม่บ่น'],
    },
    // The stream's east bank on the plains: the four envoys who watched the final, and the rival turned friend.
    { id: 'gu', zone: P, from: OD.bamboo, name: 'ผู้จดกู้ซื่อ', title: 'หอหมึกสน · ผู้จดของกรรมการ', sprite: 'scholar', at: [154.5, 135.5], school: 'pine-ink', idle: ['ทุกยกที่เจ้าสู้ ข้าบันทึกไว้แล้ว'] },
    { id: 'qi', zone: P, from: OD.bamboo, name: 'ฉีอวิ๋น', title: 'ยอดหมอก', sprite: 'taoist', at: [156.5, 134.5], school: 'mist-peak', idle: ['ข้าเห็นเจ้าพุ่งผ่านคมถังเฟย ยอดหมอกสอนให้ไม่ต้องรับเลย'] },
    { id: 'song', zone: P, from: OD.bamboo, name: 'ซ่งหลาน', title: 'คลื่นไผ่', sprite: 'fisherOld', at: [158.5, 135.5], school: 'bamboo-tide', idle: ['เจ้าแพ้แล้วกลับมาท้าใหม่ทุกครั้ง แบบนั้นแหละคลื่น'] },
    { id: 'peng', zone: P, from: OD.bamboo, name: 'หัวหน้ายามโคมเผิงต้า', title: 'ทางโคมเหล็ก', sprite: 'guardOfficer', at: [155.5, 139.5], school: 'lantern-road', idle: ['เมืองโคมต้องมีคนเฝ้าโคม ทางค้าก็เหมือนกัน'] },
    {
      id: 'he2',
      zone: P,
      from: OD.bamboo,
      name: HE,
      title: 'ตัวรองคณะงิ้ว · ตามมาส่ง',
      sprite: 'heroWudangPalm',
      at: [157.5, 139.5],
      wanderer: true,
      idle: ['คณะงิ้วเดินทางทั้งปีโดยไม่มีสำนัก ข้าว่าเจ้าก็ไปได้'],
    },
  ],
  spots: {
    basin: { at: [21.5, 13.5], r: 1.8 },
    mark1: { at: [70.5, 44.5], r: 2 },
    roost: { at: [82, 21], r: 6 },
    horseRide: { at: [16.5, 70.5], r: 5, mount: STARTER_HORSE },
    gate: { at: [48.5, 4.5], r: 5, mount: STARTER_HORSE },
    logMid: { ...LOG_SPOT, mount: STARTER_LOG },
    eastBank: { at: [156.5, 137.5], r: 3 },
  },
  routes: {
    chase: {
      id: 'chase',
      points: [
        { at: [70.5, 44.5], r: 2 },
        { at: [84.5, 34.5], r: 2 },
        { at: [82.5, 23.5], r: 2.5 },
      ],
      resetTo: [61.5, 49.5],
      failText: 'หัวขโมยหายลับไปแล้ว · ตั้งต้นใหม่ที่หน้าโรงฝึกยุทธ์',
    },
  },
  escorts: {},
  lines: {
    wandererPick: 'ดี งั้นเดินไปด้วยกันสักพัก คณะงิ้วไม่มีสำนักก็อยู่รอดมาได้สามชั่วคน ถ้าเบื่อเมื่อไร ลานเทศกาลยังจำชื่อเจ้าได้',
    joined: 'ดี ข้าบันทึกชื่อเจ้าแล้ว',
    chosenAlready: 'เจ้าเลือกทางแล้ว ข้ายินดีด้วย',
    allDone: 'ไปเถอะ ยุทธภพกว้างกว่าเวทีงิ้วนัก',
  },
};
